import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import i18n from "../../i18n";
import getAPIbaseURL from "../../utils/helpers";


const API_URL = `${getAPIbaseURL()}/projects`;

// Fetch all projects
export const fetchProjects = createAsyncThunk(
    "projects/fetchProjects",
    async (_, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}/`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.projectsSlice.fetchedError"));
        }
    }
);

// Fetch a project by id
export const fetchProject = createAsyncThunk(
    "projects/fetchProject",
    async (id, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.projectsSlice.fetchError"));
        }
    }
);


// Create project
export const createProject = createAsyncThunk(
    "projects/createProject",
    async (projectData, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.post(`${API_URL}/`, projectData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success(i18n.t("slices.projectsSlice.createdSuccess"));
            return response.data;
        } catch (error) {
            toast.error(i18n.t("slices.projectsSlice.createdError"));
            return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.projectsSlice.createdError"));
        }
    }
);

// Update project
export const updateProject = createAsyncThunk(
    "projects/updateProject",
    async (editedProject, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.put(`${API_URL}/${editedProject.id}`, editedProject, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success(i18n.t("slices.projectsSlice.updatedSuccess"));
            return response.data;
        } catch (error) {
            toast.error(i18n.t("slices.projectsSlice.updatedError"));
            return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.projectsSlice.updatedError"));
        }
    }
);

// Delete project
export const deleteProject = createAsyncThunk(
    "projects/deleteProject",
    async (projectId, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            await axios.delete(`${API_URL}/${projectId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success(i18n.t("slices.projectsSlice.deletedSuccess"));
            return projectId;
        } catch (error) {
            toast.error(i18n.t("slices.projectsSlice.deletedError"));
            return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.projectsSlice.deletedError"));
        }
    }
);

// Fetches all members assigned to a project
export const fetchProjectMembers = createAsyncThunk(
    "projects/fetchProjectMembers",
    async (projectId, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}/${projectId}/members`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return { projectId, members: response.data };
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.projectsSlice.membersFetchedError"));
        }
    }
);


// Assign a user to a project
export const addProjectMember = createAsyncThunk(
    "projects/addProjectMember",
    async ({ projectId, projectMember }, thunkAPI) => {
        const memberPayload = projectMember;

        if (!projectId || !memberPayload?.user_id) {
            return thunkAPI.rejectWithValue(i18n.t("slices.projectsSlice.memberAddedError"));
        }

        try {
            const token = localStorage.getItem("token");
            const response = await axios.post(
                `${API_URL}/${projectId}/members`,
                memberPayload,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            toast.success(i18n.t("slices.projectsSlice.memberAddedSuccess"));
            return { projectId: projectId, member: response.data };
        } catch (error) {
            toast.error(i18n.t("slices.projectsSlice.memberAddedError"));
            return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.projectsSlice.memberAddedError"));
        }
    }
);


// update project membership
export const updateProjectMember = createAsyncThunk(
    "projects/updateProjectMember",
    async ({ projectId, userId, projectMember }, thunkAPI) => {
        const memberPayload = projectMember;

        if (!projectId || !memberPayload?.user_id) {
            return thunkAPI.rejectWithValue(i18n.t("slices.projectsSlice.memberAddedError"));
        }

        try {
            const token = localStorage.getItem("token");
            const response = await axios.put(
                `${API_URL}/${projectId}/members/${userId}`,
                memberPayload,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            toast.success(i18n.t("slices.projectsSlice.memberUpdatedSuccess"));
            return { projectId: projectId, member: response.data };
        } catch (error) {
            toast.error(i18n.t("slices.projectsSlice.memberUpdatedError"));
            return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.projectsSlice.memberUpdatedError"));
        }
    }
);

// Removes a user from a project (to archived status)
export const deleteProjectMember = createAsyncThunk(
    "projects/deleteProjectMember",
    async ({ projectId, teamId, userId }, thunkAPI) => {
        const resolvedProjectId = projectId || teamId;
        if (!resolvedProjectId || !userId) {
            return thunkAPI.rejectWithValue(i18n.t("slices.projectsSlice.memberDeletedError"));
        }

        try {
            const token = localStorage.getItem("token");
            await axios.post(
                `${API_URL}/${resolvedProjectId}/members/${userId}`,
                null,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(i18n.t("slices.projectsSlice.memberDeletedSuccess"));
            return { projectId: resolvedProjectId, userId };
        } catch (error) {
            toast.error(i18n.t("slices.projectsSlice.memberDeletedError"));
            return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.projectsSlice.memberDeletedError"));
        }
    }
);


// Fetches all projects where the user has a membership
export const fetchUserProjects = createAsyncThunk(
    "projects/fetchUserProjects",
    async (userId, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}/user/${userId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.projectsSlice.userProjectsFetchedError"));
        }
    }
);




const projectsSlice = createSlice({
    name: "projects",
    initialState: {
        items: [],
        item: null,
        userProjects: {},
        isLoading: false,
        error: null,
        message: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder

            .addCase(fetchProjects.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchProjects.fulfilled, (state, action) => {
                state.isLoading = false;
                state.items = action.payload;
            })
            .addCase(fetchProjects.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.message = i18n.t("slices.projectsSlice.fetchedError");
            })


            .addCase(fetchProject.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchProject.fulfilled, (state, action) => {
                state.isLoading = false;
                state.item = action.payload;
            })
            .addCase(fetchProject.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.message = i18n.t("slices.projectsSlice.fetchError");
            })


            .addCase(createProject.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(createProject.fulfilled, (state, action) => {
                state.isLoading = false;
                state.items.push(action.payload);
                state.item = action.payload;
                state.message = i18n.t("slices.projectsSlice.createdSuccess");
            })
            .addCase(createProject.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.message = i18n.t("slices.projectsSlice.createdError");
            })

            .addCase(updateProject.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateProject.fulfilled, (state, action) => {
                state.isLoading = false;
                state.items = state.items.map((project) =>
                    project.id === action.payload.id ? action.payload : project
                );
                state.item = action.payload;
                state.message = i18n.t("slices.projectsSlice.updatedSuccess");
            })
            .addCase(updateProject.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.message = i18n.t("slices.projectsSlice.updatedError");
            })

            .addCase(deleteProject.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deleteProject.fulfilled, (state, action) => {
                state.isLoading = false;
                state.items = state.items.filter((t) => t.id !== action.payload);
                state.item = null;
                state.message = i18n.t("slices.projectsSlice.deletedSuccess");
            })
            .addCase(deleteProject.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.message = i18n.t("slices.projectsSlice.deletedError");
            })

            .addCase(fetchProjectMembers.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchProjectMembers.fulfilled, (state, action) => {
                state.isLoading = false;
                state.items = state.items.map((project) => {
                    if (String(project.id) === String(action.payload.projectId)) {
                        return { ...project, members: action.payload.members };
                    } else {
                        return project;
                    }
                });
                if (String(state.item?.id) === String(action.payload.projectId)) {
                    state.item = { ...state.item, members: action.payload.members };
                }

            })
            .addCase(fetchProjectMembers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.message = i18n.t("slices.projectsSlice.membersFetchedError");
            })

            .addCase(addProjectMember.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(addProjectMember.fulfilled, (state, action) => {
                state.isLoading = false;
                const projectId = action.payload.projectId;
                const member = action.payload.member;

                state.items = state.items.map((project) => {
                    if (String(project.id) === String(projectId)) {
                        const members = project.members || [];
                        const hasMember = members.some(
                            (existingMember) => String(existingMember.user_id) === String(member.user_id)
                        );

                        if (hasMember) {
                            return {
                                ...project,
                                members: members.map((existingMember) =>
                                    String(existingMember.user_id) === String(member.user_id)
                                        ? member
                                        : existingMember
                                ),
                            };
                        }

                        return { ...project, members: [...members, member] };
                    } else {
                        return project;
                    }
                });

                if (String(state.item?.id) === String(projectId)) {
                    const itemMembers = state.item.members || [];
                    const hasMember = itemMembers.some(
                        (existingMember) => String(existingMember.user_id) === String(member.user_id)
                    );

                    state.item = {
                        ...state.item,
                        members: hasMember
                            ? itemMembers.map((existingMember) =>
                                String(existingMember.user_id) === String(member.user_id)
                                    ? member
                                    : existingMember
                            )
                            : [...itemMembers, member],
                    };
                }

                state.message = i18n.t("slices.projectsSlice.memberAddedSuccess");
            })
            .addCase(addProjectMember.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.message = i18n.t("slices.projectsSlice.memberAddedError");
            })

            .addCase(deleteProjectMember.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deleteProjectMember.fulfilled, (state, action) => {
                state.isLoading = false;
                state.items = state.items.map((project) => {
                    if (String(project.id) !== String(action.payload.projectId)) {
                        return project;
                    }

                    return {
                        ...project,
                        members: (project.members || []).filter(
                            (member) => String(member.user_id) !== String(action.payload.userId)
                        ),
                    };
                });

                if (String(state.item?.id) === String(action.payload.projectId)) {
                    state.item = {
                        ...state.item,
                        members: (state.item.members || []).filter(
                            (member) => String(member.user_id) !== String(action.payload.userId)
                        ),
                    };
                }

                state.message = i18n.t("slices.projectsSlice.memberDeletedSuccess");
            })
            .addCase(deleteProjectMember.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.message = i18n.t("slices.projectsSlice.memberDeletedError");
            })


            .addCase(fetchUserProjects.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchUserProjects.fulfilled, (state, action) => {
                state.isLoading = false;
                state.userProjects = {
                    ...state.userProjects,
                    [action.meta.arg]: action.payload,
                };
            })
            .addCase(fetchUserProjects.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.message = i18n.t("slices.projectsSlice.userProjectsFetchedError");
            });
    },
});

export default projectsSlice.reducer;
